import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Download, Receipt, Send, AlertCircle, CheckCircle2, Clock, ArrowUpDown, FileWarning, FileCheck2, CheckCheck, RefreshCcw, Ban, MailCheck, BadgeEuro, CreditCard, Copy, Loader2 } from 'lucide-react';
import api from '../api/client';
import QuoteFormDialog from '../components/QuoteFormDialog';
import DetailSheet from '../components/DetailSheet';
import StatusPipeline from '../components/StatusPipeline';
import RowActions from '../components/RowActions';
import EmptyState from '../components/EmptyState';
import { SkeletonRow } from '../components/Skeleton';
import BulkActionsBar from '../components/BulkActionsBar';
import ConfirmDialog from '../components/ConfirmDialog';
import InlineStatus from '../components/InlineStatus';
import CreditNoteDialog from '../components/CreditNoteDialog';
import RepriseEditionDialog from '../components/RepriseEditionDialog';
import { exportToCsv, downloadPdf } from '../utils/export';
import { toast } from '../hooks/use-toast';
import { useLastSeen, useAutoMarkSeen } from '../hooks/useLastSeen';
import { useBulkSelectionKeyboard } from '../hooks/useBulkSelectionKeyboard';
import FacturesKPICards from '../components/factures/FacturesKPICards';
import FacturesToolbar from '../components/factures/FacturesToolbar';
import FacturesProvisoireBanner from '../components/factures/FacturesProvisoireBanner';
import PeriodFilter, { periodCutoffDate } from '../components/PeriodFilter';

const STATUS_CONFIG = {
  'Provisoire': { style: 'bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA]', icon: FileWarning },
  'Validée': { style: 'bg-[#E0F2FE] text-[#0369A1]', icon: CheckCheck },
  'Payée': { style: 'bg-[#E6FBF7] text-[#0F766E]', icon: CheckCircle2 },
  'En attente': { style: 'bg-[#FEF3E7] text-[#B45309]', icon: Clock },
  'En retard': { style: 'bg-[#FEF2F2] text-[#B91C1C]', icon: AlertCircle },
  'Annulée': { style: 'bg-[#F3F4F8] text-[#6B6C85]', icon: Ban },
};
const STATUS_STYLES = Object.fromEntries(Object.entries(STATUS_CONFIG).map(([k, v]) => [k, v.style]));

export default function Factures() {
  useAutoMarkSeen('invoices_unpaid', 3000);
  const { isNew } = useLastSeen();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('Toutes');
  // Advanced filters
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterMinAmount, setFilterMinAmount] = useState('');
  const [filterMaxAmount, setFilterMaxAmount] = useState('');
  // Vague 99.4 — Quick period filter (recomputes KPIs)
  const [period, setPeriod] = useState('all');
  const [invoices, setInvoices] = useState([]);
  const [create, setCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [selected, setSelected] = useState(new Set());
  const [bulkValidateOpen, setBulkValidateOpen] = useState(false);
  const [validateOne, setValidateOne] = useState(null);
  const [creditNoteFor, setCreditNoteFor] = useState(null);
  const [repriseFor, setRepriseFor] = useState(null);
  const [deleteProv, setDeleteProv] = useState(null);
  const [bulkDeleteProvOpen, setBulkDeleteProvOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.get('/invoices').then(({ data }) => setInvoices(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Deep-link support — /app/factures?focus=ID opens that invoice's DetailSheet,
  // and /app/factures?status=Payée presets the status filter (Vague 99.2 — KPI tile clickable).
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const status = params.get('status');
    if (status && ['Toutes', 'Provisoire', 'Validée', 'Payée', 'En retard', 'Annulée'].includes(status)) {
      setFilter(status);
    }
  }, [location.search]);

  useEffect(() => {
    if (!invoices.length) return;
    const params = new URLSearchParams(location.search || '');
    const fid = params.get('focus');
    if (!fid) return;
    const found = invoices.find((i) => i.id === fid);
    if (found) {
      setDetail(found);
      navigate(location.pathname, { replace: true });
    }
  }, [invoices, location.search, location.pathname, navigate]);

  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = (ids) => setSelected((s) => s.size === ids.length ? new Set() : new Set(ids));

  const bulkStatus = async (status) => {
    const ids = Array.from(selected);
    // Bulk-status is only safe for already-validated invoices (Payée/En retard). Block Provisoire.
    const provisoires = invoices.filter((i) => selected.has(i.id) && i.status === 'Provisoire');
    if (provisoires.length) {
      toast({ title: 'Action refusée', description: 'Validez les factures provisoires avant de changer leur statut.' });
      return;
    }
    await api.post('/bulk/status', { collection: 'invoices', ids, status });
    setInvoices((prev) => prev.map((i) => selected.has(i.id) ? { ...i, status } : i));
    setSelected(new Set());
    toast({ title: 'Statut mis à jour', description: `${ids.length} → ${status}` });
  };

  const bulkExport = () => {
    const rows = invoices.filter((i) => selected.has(i.id));
    exportToCsv('factures-selection', rows, ['id','client','amount','date','dueDate','status']);
    toast({ title: 'Export CSV', description: `${rows.length} lignes` });
  };

  const runBulkValidate = async () => {
    const ids = Array.from(selected).filter((id) => {
      const inv = invoices.find((x) => x.id === id);
      return inv?.status === 'Provisoire';
    });
    if (ids.length === 0) { toast({ title: 'Aucune facture provisoire sélectionnée' }); throw new Error(); }
    try {
      const { data } = await api.post('/invoices/bulk-validate', { ids });
      const { data: fresh } = await api.get('/invoices');
      setInvoices(fresh);
      setSelected(new Set());
      toast({
        title: `${data.validated.length} facture${data.validated.length > 1 ? 's' : ''} validée${data.validated.length > 1 ? 's' : ''}`,
        description: data.validated.length ? `Nouveaux n° : ${data.validated.map((v) => v.new_id).join(', ')}` : 'Aucune validation effectuée',
      });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Validation groupée impossible' });
      throw new Error();
    }
  };

  const bulkSend = async () => {
    const ids = Array.from(selected);
    try {
      const { data } = await api.post('/invoices/bulk-send', { ids });
      const okCount = (data.sent || []).filter((s) => s.ok).length;
      const errs = data.errors || [];
      const errCount = errs.length;
      // « 3 erreurs » sans la raison fait ouvrir les trois factures une par
      // une : on nomme la première, qui est presque toujours la même.
      toast({
        title: errCount && !okCount ? "Aucune facture n'est partie" : 'Envoi terminé',
        description: `${okCount} envoyée${okCount > 1 ? 's' : ''}${
          errCount ? ` · ${errCount} erreur${errCount > 1 ? 's' : ''} — ${errs[0].error}` : ''
        }`,
      });
      setSelected(new Set());
    } catch {
      toast({ title: 'Erreur', description: 'Envoi groupé impossible.' });
    }
  };

  const handleValidateOne = async () => {
    try {
      const { data } = await api.post(`/invoices/${validateOne.id}/validate`);
      setInvoices((prev) => [data, ...prev.filter((x) => x.id !== validateOne.id)]);
      if (detail?.id === validateOne.id) setDetail(data);
      toast({ title: 'Facture validée', description: `${validateOne.id} → ${data.id}` });
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det && typeof det === 'object' && Array.isArray(det.errors)) {
        toast({ title: det.message || 'Incohérences bloquantes', description: det.errors.slice(0, 3).join(' · ') });
      } else {
        toast({ title: 'Erreur', description: typeof det === 'string' ? det : 'Validation impossible' });
      }
      throw new Error();
    }
  };

  const handleDeleteProvisoire = async (f) => {
    try {
      await api.delete(`/invoices/${f.id}`);
      setInvoices((prev) => prev.filter((x) => x.id !== f.id));
      if (detail?.id === f.id) setDetail(null);
      toast({ title: 'Facture provisoire supprimée', description: f.id });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Suppression impossible' });
      throw new Error();
    }
  };

  const bulkDeleteProv = async () => {
    const ids = Array.from(selected).filter((id) => invoices.find((x) => x.id === id)?.status === 'Provisoire');
    if (ids.length === 0) { toast({ title: 'Aucune provisoire sélectionnée' }); throw new Error(); }
    try {
      const { data } = await api.post('/bulk/delete', { collection: 'invoices', ids });
      setInvoices((prev) => prev.filter((x) => !ids.includes(x.id)));
      setSelected(new Set());
      toast({ title: `${data.deleted} facture${data.deleted > 1 ? 's' : ''} supprimée${data.deleted > 1 ? 's' : ''}`, description: data.blocked?.length ? `${data.blocked.length} validée${data.blocked.length > 1 ? 's' : ''} ignorée${data.blocked.length > 1 ? 's' : ''}.` : undefined });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Suppression impossible' });
      throw new Error();
    }
  };

  const handleDownload = async (f) => {
    try {
      await downloadPdf(api, `/invoices/${f.id}/pdf`, `facture-${f.id}`);
      toast({ title: 'PDF téléchargé', description: `facture-${f.id}.pdf` });
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de générer le PDF.' });
    }
  };

  const handleRealRemind = async (f) => {
    if (f.status === 'Provisoire') {
      toast({ title: 'Relance impossible', description: 'Validez d\'abord la facture.' });
      return;
    }
    try {
      const { data } = await api.post(`/invoices/${f.id}/remind`);
      if (data.email_sent) {
        const sentAt = data.email_sent_at || new Date().toISOString();
        setInvoices((prev) => prev.map((x) => x.id === f.id ? {
          ...x,
          email_sent_at: sentAt,
          email_sent_to: data.sent_to || x.email_sent_to,
        } : x));
        if (detail?.id === f.id) {
          setDetail((d) => d ? { ...d, email_sent_at: sentAt, email_sent_to: data.sent_to || d.email_sent_to } : d);
        }
        toast({ title: 'Relance envoyée ✅', description: `→ ${data.sent_to}` });
      } else {
        toast({
          title: 'Envoi échoué',
          description: data.email_error
            ? `${data.email_error} (destinataire prévu : ${data.sent_to})`
            : `Email non envoyé (SMTP/Resend non configuré). Destinataire prévu : ${data.sent_to}`,
        });
      }
      if (detail?.id === f.id) loadReminders(f.id);
    } catch (e) {
      toast({ title: 'Relance impossible', description: e?.response?.data?.detail || 'Erreur' });
    }
  };

  const [reminders, setReminders] = useState([]);
  const [paymentLinkBusy, setPaymentLinkBusy] = useState(false);

  const handleSendPaymentLink = async (f) => {
    if (paymentLinkBusy) return;
    setPaymentLinkBusy(true);
    try {
      const { data } = await api.post(`/invoices/${f.id}/payment-link`, { send_email: true });
      const updates = {
        payment_link_url: data.url,
        payment_link_id: data.id,
        payment_link_created_at: data.created_at || f.payment_link_created_at,
        payment_link_sent_at: data.sent_at || f.payment_link_sent_at,
        payment_link_sent_to: data.email_to || f.payment_link_sent_to,
      };
      setInvoices((prev) => prev.map((x) => x.id === f.id ? { ...x, ...updates } : x));
      if (detail?.id === f.id) setDetail((d) => d ? { ...d, ...updates } : d);
      if (data.email_sent) {
        toast({ title: 'Lien de paiement envoyé ✅', description: `→ ${data.email_to}${data.reused ? ' (lien existant réutilisé)' : ''}` });
      } else {
        toast({ title: 'Lien généré', description: data.email_error || 'Email non envoyé — copiez le lien manuellement.' });
      }
    } catch (e) {
      const detailMsg = e?.response?.data?.detail || 'Création du lien impossible';
      toast({ title: 'Erreur Stripe', description: detailMsg });
    } finally {
      setPaymentLinkBusy(false);
    }
  };

  const handleCopyPaymentLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Lien copié', description: url });
    } catch {
      toast({ title: 'Copie impossible', description: 'Sélectionnez et copiez le lien manuellement.' });
    }
  };

  const handleRevokePaymentLink = async (f) => {
    if (!window.confirm('Désactiver ce lien de paiement ? Le client ne pourra plus l\'utiliser.')) return;
    try {
      await api.delete(`/invoices/${f.id}/payment-link`);
      const cleaned = { ...f };
      delete cleaned.payment_link_url;
      delete cleaned.payment_link_id;
      delete cleaned.payment_link_created_at;
      delete cleaned.payment_link_sent_at;
      delete cleaned.payment_link_sent_to;
      setInvoices((prev) => prev.map((x) => x.id === f.id ? cleaned : x));
      if (detail?.id === f.id) setDetail(cleaned);
      toast({ title: 'Lien désactivé' });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' });
    }
  };

  // Vague 113 L4 — Manual Stripe reconciliation when the webhook missed.
  // Polls the PaymentLink's checkout sessions and flips the invoice to
  // "Payée" if any session is complete+paid. Idempotent.
  const [reconcileBusy, setReconcileBusy] = useState(null);
  const handleReconcileStripe = async (f) => {
    setReconcileBusy(f.id);
    try {
      const { data } = await api.post(`/invoices/${f.id}/reconcile-stripe`);
      if (data.already_paid) {
        toast({ title: 'Déjà payée', description: `Paiement reçu le ${new Date(data.paid_at).toLocaleString('fr-FR')}` });
      } else if (data.paid) {
        toast({ title: 'Paiement confirmé ✅', description: 'Facture marquée Payée via Stripe.' });
        // Refresh local list/detail.
        const patch = { status: 'Payée', paid_at: data.paid_at, paid_via: 'stripe' };
        setInvoices((prev) => prev.map((x) => x.id === f.id ? { ...x, ...patch } : x));
        if (detail?.id === f.id) setDetail((d) => d ? { ...d, ...patch } : d);
      } else {
        toast({ title: 'Pas encore payée', description: data.message || 'Aucun paiement finalisé côté Stripe.' });
      }
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec de la vérification.' });
    } finally {
      setReconcileBusy(null);
    }
  };

  const loadReminders = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}/reminders`);
      setReminders(data);
    } catch { setReminders([]); }
  };
  useEffect(() => { if (detail?.id) loadReminders(detail.id); else setReminders([]); }, [detail?.id]);

  const filtered = useMemo(() => {
    let arr = invoices;
    if (filter === 'Avoirs') {
      arr = arr.filter((i) => i.kind === 'credit_note');
    } else if (filter !== 'Toutes') {
      arr = arr.filter((i) => i.status === filter && i.kind !== 'credit_note');
    }
    const cutoff = periodCutoffDate(period);
    arr = arr.filter((i) => {
      if (search && !`${i.id} ${i.client}`.toLowerCase().includes(search.toLowerCase())) return false;
      if (cutoff && i.date && i.date < cutoff) return false;
      if (filterDateFrom && i.date && i.date < filterDateFrom) return false;
      if (filterDateTo && i.date && i.date > filterDateTo) return false;
      const amt = Number(i.amount) || 0;
      if (filterMinAmount && amt < Number(filterMinAmount)) return false;
      if (filterMaxAmount && amt > Number(filterMaxAmount)) return false;
      return true;
    });
    return [...arr].sort((a, b) => {
      const va = a[sort.key] ?? ''; const vb = b[sort.key] ?? '';
      if (typeof va === 'number') return sort.dir === 'asc' ? va - vb : vb - va;
      return sort.dir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [invoices, search, filter, sort, filterDateFrom, filterDateTo, filterMinAmount, filterMaxAmount, period]);

  useBulkSelectionKeyboard({
    items: filtered,
    selected,
    setSelected,
    onDelete: () => toast({ title: 'Suppression interdite', description: 'Les factures ne peuvent pas être supprimées. Émettez un avoir si nécessaire.' }),
  });

  // Vague 99.4 — KPIs use the period-aware base (`invoices` clipped by period
  // when active). The status filter chips don't influence KPIs (a user
  // browsing "Provisoires only" still wants to see global CA totals); only
  // the date period does.
  const periodCutoff = periodCutoffDate(period);
  const periodScopedInvoices = useMemo(() => {
    if (!periodCutoff) return invoices;
    return invoices.filter((i) => !i.date || i.date >= periodCutoff);
  }, [invoices, periodCutoff]);

  // Totals exclude Provisoire (non-accounting). Credit notes (avoirs) are deducted.
  const invoicesOnly = periodScopedInvoices.filter((i) => i.kind !== 'credit_note' && i.status !== 'Provisoire' && i.status !== 'Annulée');
  const creditNotes = periodScopedInvoices.filter((i) => i.kind === 'credit_note');
  const creditTotal = creditNotes.reduce((s, c) => s + (c.amount || 0), 0);
  const paid = invoicesOnly.filter((i) => i.status === 'Payée').reduce((s, i) => s + (i.amount || 0), 0);
  const pending = invoicesOnly.filter((i) => i.status === 'Validée' || i.status === 'En attente').reduce((s, i) => s + (i.amount || 0), 0);
  const late = invoicesOnly.filter((i) => i.status === 'En retard').reduce((s, i) => s + (i.amount || 0), 0);
  const caNet = paid + pending + late - creditTotal;
  const provisoireCount = periodScopedInvoices.filter((i) => i.status === 'Provisoire').length;

  const selectedProvisoires = invoices.filter((i) => selected.has(i.id) && i.status === 'Provisoire').length;

  const SortTh = ({ label, k }) => (
    <th className="py-3.5 px-5 cursor-pointer select-none" onClick={() => toggleSort(k)}>
      <span className="inline-flex items-center gap-1">{label}<ArrowUpDown className={`w-3 h-3 ${sort.key === k ? 'text-[#4F46E5]' : 'text-[#D4D5E0]'}`} /></span>
    </th>
  );

  return (
    <div className="space-y-5 flex flex-col h-[calc(100vh-72px)]">
      <FacturesKPICards caNet={caNet} paid={paid} pending={pending} late={late} creditTotal={creditTotal} />

      <FacturesProvisoireBanner count={provisoireCount} onFilter={() => setFilter('Provisoire')} />

      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[12px] font-semibold text-[#6B6C85]">Période :</span>
        <PeriodFilter value={period} onChange={setPeriod} testId="factures-period" />
      </div>

      <FacturesToolbar
        search={search}
        setSearch={setSearch}
        filter={filter}
        setFilter={setFilter}
        filterDateFrom={filterDateFrom}
        setFilterDateFrom={setFilterDateFrom}
        filterDateTo={filterDateTo}
        setFilterDateTo={setFilterDateTo}
        filterMinAmount={filterMinAmount}
        setFilterMinAmount={setFilterMinAmount}
        filterMaxAmount={filterMaxAmount}
        setFilterMaxAmount={setFilterMaxAmount}
        filteredCount={filtered.length}
        totalCount={invoices.length}
        filtered={filtered}
        onCreate={() => setCreate(true)}
      />

      <div className="soft-card overflow-hidden flex-1 min-h-0 flex flex-col" data-testid="factures-table-wrap">
        <div className="overflow-y-auto flex-1 min-h-0">
        <table className="w-full">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="text-left text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] border-b border-[#EFF0F6]">
              <th className="py-3.5 pl-5 pr-1 w-10">
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && selected.size === filtered.length}
                  onChange={() => toggleSelectAll(filtered.map((f) => f.id))}
                  className="w-4 h-4 rounded accent-[#4F46E5] cursor-pointer"
                  data-testid="invoices-select-all"
                />
              </th>
              <SortTh label="N° Facture" k="id" />
              <SortTh label="Client" k="client" />
              <SortTh label="Montant TTC" k="amount" />
              <SortTh label="Émise le" k="date" />
              <th className="py-3.5 px-5">Échéance</th>
              <SortTh label="Statut" k="status" />
              <th className="py-3.5 px-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={8} />)}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8}><EmptyState icon={Receipt} title="Aucune facture" description="Créez une facture pour commencer votre suivi." action={<button onClick={() => setCreate(true)} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold">Nouvelle facture</button>} /></td></tr>
            )}
            {!loading && filtered.map((f) => {
              const cfg = STATUS_CONFIG[f.status] || STATUS_CONFIG['En attente'];
              const Icon = cfg.icon;
              const itemIsNew = isNew('invoices_unpaid', f.created_at);
              const isProv = f.status === 'Provisoire';
              const isCredit = f.kind === 'credit_note';
              const canCreditNote = !isProv && !isCredit && f.status !== 'Annulée' && Number(f.credited_amount || 0) < Number(f.amount || 0);
              return (
                <tr key={f.id} onClick={() => setDetail(f)} className={`group data-row border-b border-[#F3F4F8] last:border-0 cursor-pointer ${selected.has(f.id) ? 'bg-[#EEF2FF]' : isCredit ? 'bg-[#FFF5F5]' : isProv ? 'bg-[#FFFBFB]' : itemIsNew ? 'bg-[#FAF6FF] hover:bg-[#EEF2FF]' : ''}`}>
                  <td className="py-4 pl-5 pr-1 w-10" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggleSelect(f.id)} data-testid={`invoice-select-${f.id}`} className="w-4 h-4 rounded accent-[#4F46E5] cursor-pointer" />
                  </td>
                  <td className="py-4 px-5">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isCredit ? 'bg-[#FEE2E2]' : isProv ? 'bg-[#FEF2F2]' : 'bg-[#E6FBF7]'}`}>
                        {isCredit ? <RefreshCcw className="w-4 h-4 text-[#B91C1C]" /> : <Icon className={`w-4 h-4 ${isProv ? 'text-[#B91C1C]' : 'text-[#0F766E]'}`} />}
                      </div>
                      <span className={`text-[13px] font-mono font-semibold ${isCredit ? 'text-[#B91C1C]' : isProv ? 'text-[#B91C1C] italic' : 'text-[#1A1B2E]'}`}>{f.id}</span>
                      {isCredit && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#B91C1C] text-white text-[9.5px] font-bold tracking-wide">AVOIR</span>}
                      {itemIsNew && !isCredit && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#4F46E5] text-white text-[9.5px] font-bold tracking-wide">NOUVEAU</span>}
                    </div>
                    {isCredit && f.source_invoice_id && (
                      <div className="text-[10.5px] text-[#8E8FA5] mt-0.5 ml-10">sur {f.source_invoice_id}{f.partial ? ' · partiel' : ''}</div>
                    )}
                  </td>
                  <td className="py-4 px-5 text-[13px] font-semibold text-[#1A1B2E]">{f.client}</td>
                  <td className={`py-4 px-5 text-[13.5px] font-bold ${isCredit ? 'text-[#B91C1C]' : 'text-[#1A1B2E]'}`}>{isCredit ? '- ' : ''}{f.amount?.toLocaleString('fr-FR')} €</td>
                  <td className="py-4 px-5 text-[13px] text-[#4A4B63]">{f.date ? new Date(f.date).toLocaleDateString('fr-FR') : '—'}</td>
                  <td className="py-4 px-5 text-[13px] text-[#4A4B63]">{f.dueDate ? new Date(f.dueDate).toLocaleDateString('fr-FR') : '—'}</td>
                  <td className="py-4 px-5">
                    {isProv ? (
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11.5px] font-semibold ${cfg.style}`}>
                        <Icon className="w-3 h-3" /> Provisoire
                      </span>
                    ) : isCredit || f.status === 'Annulée' ? (
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11.5px] font-semibold ${cfg.style}`}>
                        <Icon className="w-3 h-3" /> {f.status}
                      </span>
                    ) : (
                      <InlineStatus
                        testId={`status-invoice-${f.id}`}
                        value={f.status}
                        options={['Validée', 'Payée', 'En retard']}
                        styles={STATUS_STYLES}
                        onChange={async (newStatus) => {
                          const prev = f.status;
                          setInvoices((list) => list.map((x) => x.id === f.id ? { ...x, status: newStatus } : x));
                          try {
                            await api.put(`/invoices/${f.id}`, { ...f, status: newStatus });
                            toast({ title: 'Statut facture', description: `${f.id} → ${newStatus}` });
                          } catch {
                            setInvoices((list) => list.map((x) => x.id === f.id ? { ...x, status: prev } : x));
                            toast({ title: 'Erreur' });
                            throw new Error();
                          }
                        }}
                      />
                    )}
                  </td>
                  <td className="py-4 px-5">
                    <div className="flex items-center gap-1 justify-end flex-nowrap" onClick={(e) => e.stopPropagation()}>
                      {isProv && (
                        <button data-testid={`facture-validate-${f.id}`} onClick={() => setValidateOne(f)} title="Valider la facture" className="h-8 px-2.5 rounded-lg bg-[#14B8A6] text-white text-[11.5px] font-semibold flex items-center gap-1 hover:bg-[#0F766E]">
                          <CheckCheck className="w-3.5 h-3.5" /> Valider
                        </button>
                      )}
                      {isProv && (
                        <button data-testid={`facture-delete-${f.id}`} onClick={() => setDeleteProv(f)} title="Supprimer la facture provisoire" className="w-8 h-8 rounded-lg hover:bg-[#FEF2F2] text-[#B91C1C] flex items-center justify-center">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/></svg>
                        </button>
                      )}
                      {canCreditNote && (
                        <button data-testid={`facture-credit-${f.id}`} onClick={() => setCreditNoteFor(f)} title="Émettre un avoir" className="h-8 px-2.5 rounded-lg bg-white border border-[#FECACA] text-[#B91C1C] text-[11.5px] font-semibold flex items-center gap-1 hover:bg-[#FEF2F2]">
                          <RefreshCcw className="w-3.5 h-3.5" /> Avoir
                        </button>
                      )}
                      <button data-testid={`facture-pdf-${f.id}`} onClick={() => handleDownload(f)} title="Télécharger PDF" className="w-8 h-8 rounded-lg hover:bg-[#F6F7FB] text-[#6B6C85] flex items-center justify-center"><Download className="w-3.5 h-3.5" /></button>
                      {f.status === 'Payée' && (
                        <span
                          data-testid={`facture-paid-${f.id}`}
                          title={f.paid_at ? `Payée le ${new Date(f.paid_at).toLocaleDateString('fr-FR')}` : 'Payée'}
                          className="w-8 h-8 rounded-lg bg-[#E6FBF7] text-[#0F766E] inline-flex items-center justify-center"
                          aria-label="Facture payée"
                        >
                          <BadgeEuro className="w-3.5 h-3.5" />
                        </span>
                      )}
                      {!isProv && !isCredit && f.status !== 'Payée' && (() => {
                        const sent = !!f.email_sent_at;
                        const tip = sent
                          ? `Relancer · Dernier envoi : ${new Date(f.email_sent_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}${f.email_sent_to ? ` à ${f.email_sent_to}` : ''}`
                          : 'Relancer par email';
                        return (
                          <button
                            data-testid={`facture-remind-${f.id}`}
                            onClick={() => handleRealRemind(f)}
                            title={tip}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${sent ? 'bg-[#E6FBF7] text-[#0F766E] hover:bg-[#CCF5EC]' : 'hover:bg-[#EEF2FF] text-[#4F46E5]'}`}
                          >
                            {sent ? <MailCheck className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
                          </button>
                        );
                      })()}
                      <RowActions onView={() => setDetail(f)} onEdit={isProv ? () => setEditing(f) : undefined} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      <QuoteFormDialog
        mode="invoice"
        open={create}
        onOpenChange={setCreate}
        onCreated={(data) => {
          setInvoices((prev) => [data, ...prev]);
          toast({ title: 'Facture créée (Provisoire)', description: `${data.id} — validez-la pour attribuer un numéro définitif.` });
        }}
      />
      <QuoteFormDialog
        mode="invoice"
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        quote={editing}
        onUpdated={(data) => {
          setInvoices((prev) => prev.map((i) => i.id === data.id ? data : i));
          setEditing(null);
          toast({ title: 'Facture modifiée', description: data.id });
        }}
      />

      <ConfirmDialog
        open={!!validateOne}
        onOpenChange={(o) => !o && setValidateOne(null)}
        title="Valider cette facture ?"
        description={`Un numéro définitif et séquentiel F-YYYY-NNNN sera attribué à ${validateOne?.id || ''}. Cette action est irréversible — le numéro fait partie de la comptabilité.`}
        confirmLabel="Valider définitivement"
        onConfirm={handleValidateOne}
      />
      <ConfirmDialog
        open={bulkValidateOpen}
        onOpenChange={setBulkValidateOpen}
        title={`Valider ${selectedProvisoires} facture${selectedProvisoires > 1 ? 's' : ''} provisoire${selectedProvisoires > 1 ? 's' : ''} ?`}
        description={`Un numéro définitif F-YYYY-NNNN sera attribué à chaque facture dans l'ordre chronologique de création. Les numéros sont séquentiels sans trous (intégrité comptable).`}
        confirmLabel={`Valider ${selectedProvisoires} facture${selectedProvisoires > 1 ? 's' : ''}`}
        onConfirm={runBulkValidate}
      />

      <ConfirmDialog
        open={!!deleteProv}
        onOpenChange={(o) => !o && setDeleteProv(null)}
        title={`Supprimer la facture provisoire ${deleteProv?.id || ''} ?`}
        description={`Cette facture n'ayant pas encore reçu de numéro comptable définitif, elle peut être supprimée sans impact sur la séquence F-YYYY-NNNN. Action irréversible.`}
        confirmLabel="Supprimer"
        onConfirm={() => handleDeleteProvisoire(deleteProv)}
      />
      <ConfirmDialog
        open={bulkDeleteProvOpen}
        onOpenChange={setBulkDeleteProvOpen}
        title={`Supprimer ${selectedProvisoires} facture${selectedProvisoires > 1 ? 's' : ''} provisoire${selectedProvisoires > 1 ? 's' : ''} ?`}
        description={`Seules les factures provisoires seront supprimées. Les factures validées de la sélection sont protégées et ignorées.`}
        confirmLabel={`Supprimer ${selectedProvisoires} provisoire${selectedProvisoires > 1 ? 's' : ''}`}
        onConfirm={bulkDeleteProv}
      />

      <RepriseEditionDialog
        open={!!repriseFor}
        onOpenChange={(o) => !o && setRepriseFor(null)}
        invoice={repriseFor}
        onConsignee={() => { if (repriseFor) handleDownload(repriseFor); }}
      />

      <CreditNoteDialog
        open={!!creditNoteFor}
        onOpenChange={(o) => !o && setCreditNoteFor(null)}
        invoice={creditNoteFor}
        onIssued={(credit) => {
          // Insert avoir + update source invoice locally
          setInvoices((prev) => {
            const updated = prev.map((x) => x.id === credit.source_invoice_id ? {
              ...x,
              credited_amount: (Number(x.credited_amount || 0) + Number(credit.amount || 0)),
              credit_note_ids: [...(x.credit_note_ids || []), credit.id],
              status: (Number(x.credited_amount || 0) + Number(credit.amount || 0)) >= Number(x.amount || 0) ? 'Annulée' : x.status,
            } : x);
            return [credit, ...updated];
          });
          if (detail?.id === credit.source_invoice_id) {
            setDetail((d) => d ? {
              ...d,
              credited_amount: (Number(d.credited_amount || 0) + Number(credit.amount || 0)),
              credit_note_ids: [...(d.credit_note_ids || []), credit.id],
              status: (Number(d.credited_amount || 0) + Number(credit.amount || 0)) >= Number(d.amount || 0) ? 'Annulée' : d.status,
            } : null);
          }
        }}
      />

      <DetailSheet
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
        title={detail?.id}
        subtitle={detail?.client}
        rows={detail ? [
          { label: 'Client', value: detail.client },
          { label: 'Montant TTC', value: `${detail.amount?.toLocaleString('fr-FR')} €` },
          { label: 'Date', value: detail.date ? new Date(detail.date).toLocaleDateString('fr-FR') : '—' },
          { label: 'Échéance', value: detail.dueDate ? new Date(detail.dueDate).toLocaleDateString('fr-FR') : '—' },
          { label: 'Statut', value: detail.status },
          ...(detail.validated_at ? [{ label: 'Validée le', value: new Date(detail.validated_at).toLocaleString('fr-FR') }] : []),
          ...(detail.email_sent_at
            ? [{
                label: 'Email envoyé',
                value: `${new Date(detail.email_sent_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}${detail.email_sent_to ? ` à ${detail.email_sent_to}` : ''}`,
              }]
            : detail.status !== 'Provisoire'
              ? [{ label: 'Email envoyé', value: '— (non envoyé)' }]
              : []),
        ] : []}
      >
        {detail && (
          <div className="mt-4 pt-4 border-t border-[#EFF0F6]">
            {detail.kind !== 'credit_note' && (
              <div className="py-2 mb-3">
                <StatusPipeline
                  tone="teal"
                  currentKey={
                    detail.status === 'Provisoire'
                      ? 'Provisoire'
                      : detail.status === 'Payée'
                      ? 'Payée'
                      : 'Validée'
                  }
                  canceled={detail.status === 'Annulée'}
                  steps={[
                    { key: 'Provisoire', label: 'Provisoire', ts: detail.created_at },
                    {
                      key: 'Validée',
                      label: 'Validée & envoyée',
                      ts: detail.validated_at,
                      clickable: detail.status === 'Provisoire',
                      onClick: () => setValidateOne(detail),
                    },
                    {
                      key: 'Payée',
                      label: 'Payée',
                      ts: detail.paid_at,
                      clickable: detail.status === 'Validée' || detail.status === 'En attente' || detail.status === 'En retard',
                      onClick: async () => {
                        try {
                          // Mark the invoice as paid manually (cash, bank
                          // transfer, etc — outside Stripe). We explicitly
                          // tag `paid_via: "manual"` so the Stripe receipt
                          // email logic (gated on `stripe_receipt_sent_at`)
                          // never fires for non-Stripe payments — see
                          // `routes/integrations.py` Vague 98.8.
                          const now = new Date().toISOString();
                          await api.put(`/invoices/${detail.id}`, {
                            ...detail,
                            status: 'Payée',
                            paid_at: now,
                            paid_via: 'manual',
                          });
                          const updated = { ...detail, status: 'Payée', paid_at: now, paid_via: 'manual' };
                          setInvoices((list) => list.map((x) => x.id === detail.id ? updated : x));
                          setDetail(updated);
                          toast({ title: 'Facture payée', description: `${detail.id} → Payée (paiement manuel — aucun reçu email envoyé)` });
                        } catch (e) {
                          toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible' });
                        }
                      },
                    },
                  ]}
                />
                {detail.status === 'En retard' && (
                  <div className="mt-2 text-[11.5px] text-[#B91C1C] font-semibold text-center">Paiement en retard</div>
                )}
                {detail.status === 'Annulée' && (
                  <div className="mt-2 text-[11.5px] text-[#B91C1C] font-semibold text-center">Facture annulée (avoir total)</div>
                )}
              </div>
            )}
            {detail.kind === 'credit_note' && (
              <div className="mb-3 p-3 rounded-xl bg-[#FEF2F2] border border-[#FECACA] text-[12.5px] text-[#7F1D1D] flex items-start gap-2">
                <RefreshCcw className="w-4 h-4 mt-0.5 shrink-0 text-[#B91C1C]" />
                <div>Avoir émis{detail.source_invoice_id ? <> sur la facture <span className="font-mono font-semibold">{detail.source_invoice_id}</span></> : null}{detail.reason ? <> — Motif : <em>{detail.reason}</em></> : null}.</div>
              </div>
            )}
            {detail.status === 'Provisoire' && (
              <div className="mb-3 p-3 rounded-xl bg-[#FEF2F2] border border-[#FECACA] text-[12.5px] text-[#7F1D1D] flex items-start gap-2">
                <FileWarning className="w-4 h-4 mt-0.5 shrink-0 text-[#B91C1C]" />
                <div>Cette facture est en <strong>statut Provisoire</strong>. Validez-la pour lui attribuer un numéro définitif et l'intégrer à la comptabilité. Le PDF actuel porte le filigrane « PROVISOIRE ».</div>
              </div>
            )}
            <div className="flex gap-2 mb-4 flex-wrap">
              {detail.status === 'Provisoire' ? (
                <button data-testid="detail-validate-btn" onClick={() => setValidateOne(detail)} className="flex-1 min-w-[45%] h-10 rounded-xl bg-[#14B8A6] hover:bg-[#0F766E] text-white text-[13px] font-semibold flex items-center justify-center gap-1.5">
                  <CheckCheck className="w-4 h-4" /> Valider définitivement
                </button>
              ) : detail.kind !== 'credit_note' && detail.status !== 'Annulée' && Number(detail.credited_amount || 0) < Number(detail.amount || 0) ? (
                <button data-testid="detail-credit-btn" onClick={() => setCreditNoteFor(detail)} className="flex-1 min-w-[45%] h-10 rounded-xl bg-white border border-[#FECACA] text-[#B91C1C] text-[13px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#FEF2F2]">
                  <RefreshCcw className="w-4 h-4" /> Émettre un avoir
                </button>
              ) : null}
              {detail.kind !== 'credit_note' && detail.status !== 'Provisoire' && (
                <button data-testid={`detail-remind-${detail.id}`} onClick={() => handleRealRemind(detail)} className="flex-1 min-w-[45%] h-10 rounded-xl bg-white border border-[#C7D2FE] text-[#4F46E5] text-[13px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#EEF2FF]">
                  <Send className="w-4 h-4" /> Relancer par email
                </button>
              )}
              {detail.kind !== 'credit_note' && ['Validée','En attente','En retard'].includes(detail.status) && (
                <button
                  data-testid={`detail-payment-link-${detail.id}`}
                  onClick={() => handleSendPaymentLink(detail)}
                  disabled={paymentLinkBusy}
                  className="flex-1 min-w-[45%] h-10 rounded-xl bg-white border border-[#D6D3FF] text-[#635BFF] text-[13px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#F4F2FF] disabled:opacity-60"
                >
                  {paymentLinkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  {detail.payment_link_url ? 'Renvoyer le lien Stripe' : 'Envoyer lien de paiement'}
                </button>
              )}
              <button onClick={() => handleDownload(detail)} className="flex-1 min-w-[45%] h-10 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center justify-center gap-1.5">
                <Download className="w-4 h-4" /> Télécharger PDF
              </button>
              {detail.status !== 'Provisoire' && (
                <button data-testid={`detail-reprise-${detail.id}`} onClick={() => setRepriseFor(detail)} className="flex-1 min-w-[45%] h-10 rounded-xl bg-white border border-[#EFF0F6] text-[#4A4B63] text-[13px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#F6F7FB]">
                  <FileCheck2 className="w-4 h-4" /> Rééditer le PDF
                </button>
              )}
            </div>
            {/* Payment link panel — Vague 98.1 */}
            {detail.kind !== 'credit_note' && detail.payment_link_url && (
              <div className="mb-4 p-3 rounded-xl bg-[#F4F2FF] border border-[#D6D3FF]" data-testid={`payment-link-panel-${detail.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-4 h-4 text-[#635BFF]" />
                  <div className="text-[12px] font-semibold text-[#635BFF] uppercase tracking-wide">Lien de paiement Stripe</div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    readOnly
                    value={detail.payment_link_url}
                    className="flex-1 h-9 px-3 rounded-lg bg-white border border-[#D6D3FF] text-[11.5px] font-mono text-[#1A1B2E]"
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    data-testid={`payment-link-copy-${detail.id}`}
                    onClick={() => handleCopyPaymentLink(detail.payment_link_url)}
                    className="h-9 px-3 rounded-lg bg-white border border-[#D6D3FF] text-[#635BFF] text-[11.5px] font-semibold inline-flex items-center gap-1 hover:bg-[#EDE8FF]"
                    title="Copier"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copier
                  </button>
                  <a
                    href={detail.payment_link_url}
                    target="_blank"
                    rel="noreferrer"
                    className="h-9 px-3 rounded-lg bg-[#635BFF] text-white text-[11.5px] font-semibold inline-flex items-center gap-1 hover:bg-[#4F46E5]"
                  >
                    Ouvrir →
                  </a>
                </div>
                <div className="flex items-center justify-between text-[11px] text-[#4A4B63] mt-1">
                  <div>
                    {detail.payment_link_sent_at
                      ? <>Envoyé le {new Date(detail.payment_link_sent_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}{detail.payment_link_sent_to ? ` à ${detail.payment_link_sent_to}` : ''}</>
                      : detail.payment_link_created_at
                        ? <>Créé le {new Date(detail.payment_link_created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} — non envoyé</>
                        : null}
                  </div>
                  <div className="flex items-center gap-3">
                    {detail.status !== 'Payée' && (
                      <button
                        data-testid={`payment-link-reconcile-${detail.id}`}
                        onClick={() => handleReconcileStripe(detail)}
                        disabled={reconcileBusy === detail.id}
                        className="text-[#047857] hover:underline font-semibold disabled:opacity-60"
                        title="Interroger Stripe pour confirmer le paiement"
                      >
                        {reconcileBusy === detail.id ? 'Vérification…' : 'Vérifier paiement'}
                      </button>
                    )}
                    <button
                      data-testid={`payment-link-revoke-${detail.id}`}
                      onClick={() => handleRevokePaymentLink(detail)}
                      className="text-[#B91C1C] hover:underline font-semibold"
                    >
                      Désactiver
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* Credit notes history */}
            {detail.credit_note_ids?.length > 0 && (
              <div className="mb-4 p-3 rounded-xl bg-[#FEF2F2] border border-[#FECACA]">
                <div className="text-[11.5px] font-semibold text-[#B91C1C] uppercase tracking-wide mb-1.5">Avoirs émis ({detail.credit_note_ids.length})</div>
                <div className="space-y-1">
                  {(detail.credit_note_ids || []).map((cnId) => {
                    const cn = invoices.find((x) => x.id === cnId);
                    if (!cn) return <div key={cnId} className="text-[11.5px] text-[#7F1D1D] font-mono">{cnId}</div>;
                    return (
                      <button
                        key={cnId}
                        onClick={() => setDetail(cn)}
                        className="w-full text-left flex items-center justify-between text-[12px] p-1.5 rounded hover:bg-white"
                      >
                        <span className="font-mono font-semibold text-[#B91C1C]">{cn.id}</span>
                        <span className="text-[#7F1D1D]">-{cn.amount?.toLocaleString('fr-FR')} € · {cn.partial ? 'partiel' : 'total'}</span>
                      </button>
                    );
                  })}
                  <div className="mt-1 pt-1 border-t border-[#FECACA] flex items-center justify-between text-[11.5px] font-semibold text-[#7F1D1D]">
                    <span>Restant crédible :</span>
                    <span>{Math.max(0, (detail.amount || 0) - (detail.credited_amount || 0)).toLocaleString('fr-FR')} €</span>
                  </div>
                </div>
              </div>
            )}
            <div>
              <div className="text-[12px] font-semibold text-[#4A4B63] mb-2">Historique de relances ({reminders.length})</div>
              {reminders.length === 0 ? (
                <div className="text-[12px] text-[#8E8FA5] italic">Aucune relance envoyée pour le moment.</div>
              ) : (
                <div className="space-y-2 max-h-[220px] overflow-auto">
                  {reminders.map((r) => (
                    <div key={r.id} data-testid={`reminder-${r.id}`} className="flex items-start gap-2 text-[12px] p-2 rounded-lg bg-[#FAFAFE] border border-[#F3F4F8]">
                      <div className="w-2 h-2 rounded-full bg-[#4F46E5] mt-1 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-[#1A1B2E] truncate">{r.to}</span>
                          <span className="text-[10.5px] text-[#8E8FA5] shrink-0 ml-2">{new Date(r.sent_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div className="text-[11px] text-[#8E8FA5]">
                          {r.trigger === 'cron' ? 'Automatique' : 'Manuelle'}
                          {r.resend_id ? ` · ${r.resend_id.slice(0, 10)}…` : ' · (email non envoyé)'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DetailSheet>

      <InvoiceBulkBar
        count={selected.size}
        selectedProvisoires={selectedProvisoires}
        onClear={() => setSelected(new Set())}
        onValidate={() => setBulkValidateOpen(true)}
        onSend={bulkSend}
        onExport={bulkExport}
        onChangeStatus={bulkStatus}
        onDelete={() => setBulkDeleteProvOpen(true)}
      />
    </div>
  );
}

/** Invoice-specific bulk bar: validate + send + export + change status. NO delete (accounting integrity). */
function InvoiceBulkBar({ count, selectedProvisoires, onClear, onValidate, onSend, onExport, onChangeStatus, onDelete }) {
  if (count === 0) return null;
  const content = (
    <div
      data-testid="bulk-actions-bar"
      className="fixed left-1/2 -translate-x-1/2 bottom-6 z-[90] bg-[#1A1B2E] text-white rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3"
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      <button onClick={onClear} className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center" title="Effacer la sélection (Échap)">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <div className="text-[13px] font-semibold">{count} sélectionnée{count > 1 ? 's' : ''}</div>
      <div className="h-5 w-px bg-white/15 mx-1" />
      {selectedProvisoires > 0 && (
        <button data-testid="bulk-validate-btn" onClick={onValidate} className="h-9 px-3 rounded-lg bg-[#14B8A6] hover:bg-[#0F766E] text-[13px] font-semibold flex items-center gap-1.5 transition-colors">
          <CheckCheck className="w-4 h-4" /> Valider ({selectedProvisoires})
        </button>
      )}
      <button data-testid="bulk-send-btn" onClick={onSend} className="h-9 px-3 rounded-lg bg-white/10 hover:bg-white/20 text-[13px] font-semibold flex items-center gap-1.5 transition-colors">
        <Send className="w-4 h-4" /> Envoyer
      </button>
      <button data-testid="bulk-export-btn" onClick={onExport} className="h-9 px-3 rounded-lg bg-white/10 hover:bg-white/20 text-[13px] font-semibold flex items-center gap-1.5 transition-colors">
        <Download className="w-4 h-4" /> Exporter
      </button>
      <div className="relative group">
        <button className="h-9 px-3 rounded-lg bg-white/10 hover:bg-white/20 text-[13px] font-semibold flex items-center gap-1.5 transition-colors">
          Statut
        </button>
        <div className="absolute bottom-full mb-2 right-0 bg-white text-[#1A1B2E] rounded-xl shadow-lg py-1 min-w-[160px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
          {['Payée', 'En retard'].map((s) => (
            <button key={s} onClick={() => onChangeStatus(s)} className="w-full text-left px-3 py-2 text-[12.5px] hover:bg-[#EEF2FF] hover:text-[#4F46E5]">
              {s}
            </button>
          ))}
        </div>
      </div>
      {selectedProvisoires > 0 && onDelete && (
        <button data-testid="bulk-delete-btn" onClick={onDelete} className="h-9 px-3 rounded-lg bg-[#EF4444] hover:bg-[#DC2626] text-[13px] font-semibold flex items-center gap-1.5 transition-colors" title="Supprimer les provisoires sélectionnées">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/></svg>
          Supprimer ({selectedProvisoires})
        </button>
      )}
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}
